import { useNavigate } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Card } from './ui/card'
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from './ui/drawer'
import { ChevronRight, Plus, X } from './icons'
import {
  STARTER_CHECKLIST_TEMPLATES,
  type StarterTemplate,
} from '@/lib/starter-checklist-templates'

interface ChooseChecklistTemplateDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function ChooseChecklistTemplateDrawer({
  isOpen,
  onClose,
}: ChooseChecklistTemplateDrawerProps) {
  const navigate = useNavigate()

  const handleStartFromScratch = () => {
    onClose()
    navigate({ to: '/operations/checklisttemplates/new' })
  }

  const handlePickStarter = (starter: StarterTemplate) => {
    onClose()
    navigate({
      to: '/operations/checklisttemplates/new',
      search: { starter: starter.slug } as any,
    })
  }

  return (
    <Drawer open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
        <DrawerHeader className="flex items-center justify-between">
          <DrawerTitle className="text-xl font-bold tracking-[-1px]">
            Choose Template
          </DrawerTitle>
          <DrawerClose asChild>
            <Button variant="secondary" size="icon" roundness="round" className="size-8">
              <X className="w-4 h-4" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="px-4 pt-2 pb-6 flex flex-col gap-6">
            <Card
              className="flex items-center gap-4 px-4 py-4 border-border-brand-bold bg-background-brand-subtle cursor-pointer"
              onClick={handleStartFromScratch}
            >
              <div className="bg-background-brand-subtle rounded-lg p-2">
                <Plus className="size-8 text-content-brand-bold" />
              </div>
              <div className="flex flex-col">
                <p className="text-base font-semibold tracking-[-0.5px]">Start from Scratch</p>
                <p className="text-sm text-content-primary-subtle">Build a custom checklist</p>
              </div>
            </Card>

            <div className="flex flex-col gap-2">
              <p className="text-base font-semibold tracking-[-0.5px] text-center">
                Start from a Template
              </p>

              <div className="flex flex-col">
                {STARTER_CHECKLIST_TEMPLATES.map((starter) => (
                  <button
                    key={starter.slug}
                    type="button"
                    onClick={() => handlePickStarter(starter)}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-grey-lightest text-left"
                  >
                    <div className="flex flex-col min-w-0">
                      <p className="text-sm font-semibold tracking-[-0.2px] truncate">
                        {starter.name}
                      </p>
                      <p className="text-xs text-content-primary-subtle truncate">
                        {starter.items.length} field{starter.items.length !== 1 ? 's' : ''} ·{' '}
                        Pre-configured template
                      </p>
                    </div>
                    <ChevronRight className="size-5 text-content-primary-subtle shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
